import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { AddMailboxDialog } from "@/components/mailboxes/AddMailboxDialog";
import { useMailboxes } from "@/hooks/useMailboxes";
import { useMailboxRules } from "@/hooks/useMailboxRules";
import { useAuth } from "@/contexts/AuthContext";
import { MailboxList } from "@/components/mailboxes/MailboxList";
import { ConsentCallbackHandler } from "@/components/mailboxes/ConsentCallbackHandler";
import { Rule } from "@/lib/types";

export default function MailboxesPage() {
  const { tenantId } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  // Custom hooks
  const { 
    mailboxes, 
    isLoading, 
    isConsentProcessing, 
    fetchMailboxes, 
    updateMailboxRulesCount, 
    processConsentCallback 
  } = useMailboxes();
  
  const {
    mailboxRules,
    isRulesLoading,
    expandedMailboxes,
    handleMailboxToggle,
    handleRuleUpdated,
    handleReorderRules,
    handleDeleteRule
  } = useMailboxRules(updateMailboxRulesCount);

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">Mailboxes & Rules</h1>
          <p className="text-muted-foreground">
            Manage your connected email accounts and configure automated email processing rules.<br />
            Rules determine when and how to send auto-replies based on incoming emails.
          </p>
        </div>
        
        <Button onClick={() => setIsDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Mailbox
        </Button>
      </div>
      
      <ConsentCallbackHandler 
        isProcessing={isConsentProcessing}
        onConsentCode={processConsentCallback}
      />
      
      <div className="rounded-lg border shadow-sm">
        <MailboxList
          mailboxes={mailboxes}
          isLoading={isLoading}
          expandedMailboxes={expandedMailboxes}
          mailboxRules={mailboxRules}
          isRulesLoading={isRulesLoading}
          tenantId={tenantId}
          onMailboxToggle={handleMailboxToggle}
          onRuleUpdated={handleRuleUpdated}
          onReorderRules={handleReorderRules}
          onMailboxDeleted={fetchMailboxes}
          onDeleteRule={handleDeleteRule}
          setIsDialogOpen={setIsDialogOpen} // Pass setIsDialogOpen to MailboxList
        />
      </div>
      
      <AddMailboxDialog 
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={fetchMailboxes}
      />
    </>
  );
}
